import { html, TemplateResult } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WidgetStateFrontEnd } from '../../../../genericDashboard/gzGenericWidget/types';
import { GzGenericWidgetContent } from '../../../../genericDashboard/gzGenericWidget';
import { DEFAULT_RM_WIDGET_TOP_ROWS } from '@nirvana/shared/constants';
import { RmTopFindingsWithSeverityPayload } from '@nirvana/shared/dtos/types/widgets/common';
import { renderText } from '../../text';
import { fillArray } from '@nirvana/shared/basic-types/array';
import { getRoutinesTranslator } from '../../../common/translations';
import { WEB_COMPONENT_GZ_RM_TOP_FINDINGS_SEVERITY_WIDGET } from '../../../../webcomponents.constants';
import { translateFn } from '../../../../utils/translator.helper';
import { NavigationHelper } from '@nirvana/shared/navigation';
import { Row, RowConfig } from '../../../../ui/gzBasicTable/types/types';
import { ColumnKeys as MisconfigurationsColumnKeys } from '../../../misconfigurationsApp/types';
import { FontSizes } from '../../../../types/common/types';
import { LinkType } from '../../../../ui/gzLinkComponent/types';
import { State } from '../../../common/constants';
import { computeMaxRowsFit, fitItemsToWidget } from '../../resize';

/**
 * @summary Type here the description of this component
 * @tag gz-rm-top-findings-severity-widget
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement(WEB_COMPONENT_GZ_RM_TOP_FINDINGS_SEVERITY_WIDGET)
export class GzRmTopFindingsSeverityWidgetWebComponent extends GzGenericWidgetContent {
  @property({ type: WidgetStateFrontEnd, attribute: true, reflect: true })
  state: WidgetStateFrontEnd;

  @property({ type: Object, attribute: true })
  payload: RmTopFindingsWithSeverityPayload = { topFindingsBySeverity: { companyId: '', total: 0, items: [] } };

  routinesTranslator = getRoutinesTranslator();

  static get styles() {
    return styles;
  }

  protected viewAll(): void {
    NavigationHelper.navigateToRiskManagement({
      company: { id: this.payload.topFindingsBySeverity?.companyId ?? '' },
      filters: [
        {
          [MisconfigurationsColumnKeys.STATE]: { in: [State.ACTIVE] },
        },
        {
          [MisconfigurationsColumnKeys.RISK_SCORE]: { range: { min: 1, max: 100 } },
        },
      ],
      section: 'findings',
      newTab: true,
    });
  }

  protected renderLoader(): TemplateResult {
    return html` <gz-loader class="pt-1" .rows="${computeMaxRowsFit(this.contentHeight)}" columns="1"></gz-loader>`;
  }

  protected renderContent(): TemplateResult {
    const items: Row[] =
      this.payload.topFindingsBySeverity?.items?.map(
        ({ id, score }): RowConfig => ({
          data: { indicatorId: id },
          cells: [
            renderText(this.routinesTranslator.translateRoutineName(id)),
            html` <gz-rm-severity .score="${score}" reverse></gz-rm-severity>`,
          ],
        })
      ) ?? [];

    const emptyItem = [renderText('...', true), html` <gz-rm-severity noHighlight reverse></gz-rm-severity>`];
    const fullItems = fitItemsToWidget(fillArray(items, DEFAULT_RM_WIDGET_TOP_ROWS, emptyItem), this.contentHeight);

    return html`
      <gz-basic-table
        style="margin-top: calc(var(--spacer)*0.3)"
        rowsClickable
        alignLastColumnRight
        .onClick="${(rowData: { indicatorId: number }) => {
          if (!rowData) {
            return;
          }

          const { indicatorId } = rowData;

          NavigationHelper.navigateToRiskManagement({
            company: { id: this.payload.topFindingsBySeverity?.companyId ?? '' },
            section: 'findings',
            filters: [
              {
                [MisconfigurationsColumnKeys.MISCONFIGURATION_NAME]: {
                  complexEnumFilter: [{ value: indicatorId }],
                },
              },
            ],
            newTab: true,
          });
        }}"
        .data="${fullItems}"
      ></gz-basic-table>

      <gz-link type="${LinkType.QUIET}" fontSize="${FontSizes.PARAGRAPH_11}" .callback=${() => this.viewAll()}>
        ${translateFn('View all')} (${this.payload.topFindingsBySeverity?.total ?? 0})
      </gz-link>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-rm-top-findings-severity-widget': GzRmTopFindingsSeverityWidgetWebComponent;
  }
}
